import React, { useState, useEffect, useRef } from 'react';
import HeroSection from './components/HeroSection';
import ProductSection from './components/ProductSection';
import Footer from './components/Footer';
import OrderModal from './components/OrderModal';
import OrderTrackingModal from './components/OrderTrackingModal';
import CartModal from './components/CartModal';
import ToastContainer from './components/ToastContainer';
import CenterToastContainer from './components/CenterToastContainer';
import ProductDetailsModal from './components/ProductDetailsModal';
import { getSettings } from './services/settings';
import { listProducts, getProduct } from './services/products';
import './index.css';

function App() {
  const [selectedProduct, setSelectedProduct] = useState(null);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDetailsOpen, setIsDetailsOpen] = useState(false);
  const [isTrackingModalOpen, setIsTrackingModalOpen] = useState(false);
  const [isCartOpen, setIsCartOpen] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [activeSection, setActiveSection] = useState('home'); // For bottom nav highlighting
  const mobileMenuRef = useRef(null);
  const [settings, setSettings] = useState({ company_name: 'ঘরোয়া খাবার', company_logo: '', whatsapp_number: '+8801734562313' });
  const [logoError, setLogoError] = useState(false);
  const [cartItems, setCartItems] = useState([]);
  const [toasts, setToasts] = useState([]);
  const [centerToasts, setCenterToasts] = useState([]);
  const productsCacheRef = useRef(null);

  // SEO-friendly product URL helpers
  const makeSlug = (p) => {
    const name = p?.name_bn || 'product';
    const idPart = (p?.id || '').toString().slice(0, 8);
    try { return `/product/${encodeURIComponent(name)}-${idPart}`; } catch { return `/product/${name}-${idPart}`; }
  };
  const parseSlugIdPart = (path) => {
    const m = path.match(/\/product\/(.+)-([A-Za-z0-9\-\_]{6,})$/);
    return m ? m[2] : null;
  };
  const openProductModalWithUrl = (product) => {
    setSelectedProduct(product);
    setIsDetailsOpen(true);
    const slug = makeSlug(product);
    if (window.location.pathname !== slug) {
      window.history.pushState({ type: 'product', id: product.id }, '', slug);
    }
  };
  const closeProductModalAndResetUrl = () => {
    setIsDetailsOpen(false);
    setSelectedProduct(null);
    if (window.location.pathname.startsWith('/product/')) {
      window.history.pushState({}, '', '/');
    }
  };

  const handleOrderClick = (product) => {
    setSelectedProduct(product);
    setIsModalOpen(true);
    // If current URL is a product slug, reset to home (avoid keeping slug while ordering)
    if (window.location.pathname.startsWith('/product/')) {
      window.history.pushState({}, '', '/');
    }
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setSelectedProduct(null);
  };

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const handleWhatsAppClick = () => {
    const message = encodeURIComponent(`হ্যালো! আমি ${settings.company_name || 'আপনার প্রতিষ্ঠান'} সম্পর্কে জানতে চাই।`);
    const whatsappUrl = `https://wa.me/${settings.whatsapp_number}?text=${message}`;
    window.open(whatsappUrl, '_blank');
    setIsMobileMenuOpen(false); // Close mobile menu
  };

  // Toast helpers
  const showToast = (message, type = 'success', duration = 3000, actionLabel, onAction) => {
    const id = Date.now() + Math.random();
    setToasts((prev) => [...prev, { id, message, type, duration, actionLabel, onAction }]);
    if (duration > 0) {
      setTimeout(() => removeToast(id), duration + 50);
    }
  };
  const removeToast = (id) => {
    setToasts((prev) => prev.filter((t) => t.id !== id));
  };

  const showCenterToast = (message, duration = 30000, actionLabel, onAction) => {
    const id = Date.now() + Math.random();
    setCenterToasts((prev) => [...prev, { id, message, duration, actionLabel, onAction }]);
    if (duration > 0) {
      setTimeout(() => removeCenterToast(id), duration + 50);
    }
  };
  const removeCenterToast = (id) => {
    setCenterToasts((prev) => prev.filter((t) => t.id !== id));
  };

  // Cart persistence
  useEffect(() => {
    try {
      const saved = localStorage.getItem('cart');
      if (saved) {
        const parsed = JSON.parse(saved);
        if (Array.isArray(parsed)) setCartItems(parsed);
      }
    } catch (_e) { }
  }, []);

  useEffect(() => {
    try {
      localStorage.setItem('cart', JSON.stringify(cartItems));
    } catch (_e) { }
  }, [cartItems]);

  const addToCart = (product) => {
    if (!product) return;
    const productId = String(product.id);
    const unitPrice = parseFloat(product.offer_price || product.price || 0) || 0;
    setCartItems((prev) => {
      const idx = prev.findIndex((it) => it.product_id === productId);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + 1 };
        return copy;
      }
      return [
        ...prev,
        {
          product_id: productId,
          name_bn: product.name_bn,
          image: product.image,
          unit_price: unitPrice,
          quantity: 1,
        },
      ];
    });
    // 30s cart toast with View Cart action
    // Custom centered toast for 30s with View Cart action
    showCenterToast('পণ্য ঝুড়িতে যোগ হয়েছে', 15000, 'ঝুড়ি দেখুন', () => setIsCartOpen(true));
  };

  const updateCartQty = (productId, qty) => {
    setCartItems((prev) => prev.map((it) => (it.product_id === String(productId) ? { ...it, quantity: qty } : it)));
  };

  const removeCartItem = (productId) => {
    setCartItems((prev) => prev.filter((it) => it.product_id !== String(productId)));
  };

  const cartCount = cartItems.reduce((sum, it) => sum + it.quantity, 0);
  const cartSubtotal = cartItems.reduce((sum, it) => sum + it.quantity * (it.unit_price || 0), 0);

  // Update page title based on settings
  useEffect(() => {
    if (settings.company_name) {
      document.title = settings.company_slogan
        ? `${settings.company_name} | ${settings.company_slogan}`
        : settings.company_name;
    }
  }, [settings.company_name, settings.company_slogan]);

  // Load settings
  useEffect(() => {
    (async () => {
      try {
        const data = await getSettings();
        if (data) {
          setSettings(data);
        }
      } catch (err) {
        console.error('Failed to load settings:', err);
      }
    })();
  }, []);

  // Reset logo error when logo URL changes
  useEffect(() => {
    setLogoError(false);
  }, [settings.company_logo]);

  // Close mobile menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (mobileMenuRef.current && !mobileMenuRef.current.contains(event.target)) {
        setIsMobileMenuOpen(false);
      }
    };

    if (isMobileMenuOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isMobileMenuOpen]);

  // Handle scroll to update active section
  useEffect(() => {
    const handleScroll = () => {
      const heroSection = document.getElementById('hero');
      const productsSection = document.getElementById('products');

      if (heroSection && productsSection) {
        const scrollPosition = window.scrollY + 200; // Offset for mobile header
        const heroBottom = heroSection.offsetTop + heroSection.offsetHeight;

        if (scrollPosition < heroBottom) {
          setActiveSection('home');
        } else {
          setActiveSection('products');
        }
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Deep link: open product modal if URL is /product/:slug
  useEffect(() => {
    (async () => {
      const path = window.location.pathname || '';
      if (path.startsWith('/product/')) {
        try {
          const idPart = parseSlugIdPart(path);
          // Try full fetch by id first (supports new full-id slugs)
          if (idPart && idPart.length > 16) {
            try {
              const p = await getProduct(idPart);
              if (p) {
                openProductModalWithUrl({
                  id: p.id,
                  name_bn: p.name_bn,
                  name_en: p.name_en,
                  description_bn: p.description_bn,
                  price: p.price,
                  offer_price: p.offer_price,
                  image: p.image_url,
                  category: p.category,
                });
                return;
              }
            } catch (_e) { }
          }

          // Fallback: load list and match by starting id (supports old 8-char slugs)
          if (!productsCacheRef.current) {
            const rows = await listProducts();
            productsCacheRef.current = rows || [];
          }
          let found = null;
          if (idPart) {
            found = productsCacheRef.current.find((p) => (p.id || '').toString().startsWith(idPart));
          }
          if (!found) {
            const raw = decodeURIComponent(path.replace('/product/', ''));
            const namePart = raw.replace(/-[A-Za-z0-9\-\_]{6,}$/, '');
            found = productsCacheRef.current.find((p) => p.name_bn === namePart);
          }
          if (found) {
            openProductModalWithUrl({
              id: found.id,
              name_bn: found.name_bn,
              name_en: found.name_en,
              description_bn: found.description_bn,
              price: found.price,
              offer_price: found.offer_price,
              image: found.image_url,
              category: found.category,
            });
          }
        } catch (e) {
          console.error('Failed to open deep-link product:', e);
        }
      }
    })();

    const onPop = () => {
      if (!window.location.pathname.startsWith('/product/')) {
        setIsDetailsOpen(false);
        setSelectedProduct(null);
      }
    };
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  return (
    <div className="min-h-screen bg-gradient-to-br from-orange-50 via-white to-orange-50">
      <ToastContainer toasts={toasts} removeToast={removeToast} />
      <CenterToastContainer toasts={centerToasts} removeToast={removeCenterToast} />
      {/* Compact Mobile Header */}
      <nav ref={mobileMenuRef} className="fixed top-0 left-0 right-0 w-full bg-white/98 backdrop-blur-md shadow-lg z-50 border-b border-orange-100 md:bg-white/95">
        <div className="max-w-7xl mx-auto px-3 sm:px-4 lg:px-8">
          <div className="flex justify-between items-center h-14 md:h-16">
            <div className="flex-shrink-0">
              {settings.company_logo && !logoError ? (
                <img
                  src={settings.company_logo}
                  alt={settings.company_name}
                  className="h-10 md:h-12 object-contain"
                  onError={() => setLogoError(true)}
                />
              ) : (
                <h1 className="text-xl md:text-2xl font-bold bg-gradient-to-r from-orange-600 to-orange-700 bg-clip-text text-transparent bengali-text">
                  {settings.company_name}
                </h1>
              )}
            </div>

            {/* Desktop Navigation */}
            <div className="hidden md:block">
              <div className="ml-10 flex items-baseline space-x-4">
                <a
                  href="#hero"
                  className="text-gray-700 hover:text-orange-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  onClick={() => setActiveSection('home')}
                >
                  হোম
                </a>
                <a
                  href="#products"
                  className="text-gray-700 hover:text-orange-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                  onClick={() => setActiveSection('products')}
                >
                  পণ্য
                </a>
                <button
                  onClick={() => setIsTrackingModalOpen(true)}
                  className="text-gray-700 hover:text-orange-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  অর্ডার ট্র্যাক করুন
                </button>
                <button
                  onClick={handleWhatsAppClick}
                  className="text-gray-700 hover:text-orange-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  <span className="flex items-center">
                    <svg className="w-4 h-4 mr-1 text-green-600" fill="currentColor" viewBox="0 0 24 24">
                      <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99 0-3.951-.488-5.688-1.44l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-2.55-.978-4.95-2.756-6.77-1.778-1.82-4.144-2.82-6.694-2.82-5.448 0-9.886 4.434-9.889 9.885-.001 2.55.978 4.95 2.756 6.77 1.778 1.82 4.144 2.82 6.694 2.82zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
                    </svg>
                    যোগাযোগ
                  </span>
                </button>
                {/* Cart button (desktop) */}
                <button
                  onClick={() => setIsCartOpen(true)}
                  className="relative text-gray-700 hover:text-orange-600 px-3 py-2 rounded-md text-sm font-medium transition-colors"
                >
                  <span className="flex items-center">
                    <svg className="w-5 h-5 mr-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l3-8H6.4M7 13L5.4 5M7 13l-2 9m12-9l2 9M9 22a1 1 0 100-2 1 1 0 000 2zm8 0a1 1 0 100-2 1 1 0 000 2z" />
                    </svg>
                    ঝুড়ি
                  </span>
                  {cartCount > 0 && (
                    <span className="absolute -top-1 -right-1 bg-orange-600 text-white text-xs rounded-full px-1.5 py-0.5">{cartCount}</span>
                  )}
                </button>
              </div>
            </div>
          </div>
        </div>
      </nav>

      {/* Main Content with Mobile Padding */}
      <main className="pt-14 md:pt-16 pb-24 md:pb-0">
        <HeroSection />
        <ProductSection onOrderClick={handleOrderClick} onAddToCart={addToCart} onViewProduct={openProductModalWithUrl} />
      </main>

      {/* Footer - Visible on All Devices */}
      <div className="pb-20 md:pb-0">
        <Footer settings={settings} />
      </div>

      {/* Mobile Bottom Navigation Bar */}
      <nav className="fixed bottom-0 left-0 right-0 w-full bg-white border-t-2 border-orange-200 shadow-2xl z-50 md:hidden safe-area-inset-bottom" style={{ boxShadow: '0 -4px 20px rgba(0, 0, 0, 0.1)' }}>
        <div className="flex justify-around items-center h-16 px-2">
          {/* Home */}
          <button
            onClick={() => {
              window.scrollTo({ top: 0, behavior: 'smooth' });
              setActiveSection('home');
            }}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-all ${activeSection === 'home' ? 'text-orange-600' : 'text-gray-500'
              }`}
          >
            <svg className={`w-6 h-6 mb-1 ${activeSection === 'home' ? 'transform scale-110' : ''} transition-transform`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 12l2-2m0 0l7-7 7 7M5 10v10a1 1 0 001 1h3m10-11l2 2m-2-2v10a1 1 0 01-1 1h-3m-6 0a1 1 0 001-1v-4a1 1 0 011-1h2a1 1 0 011 1v4a1 1 0 001 1m-6 0h6" />
            </svg>
            <span className="text-xs font-medium bengali-text">হোম</span>
            {activeSection === 'home' && (
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-orange-600 rounded-t-full"></div>
            )}
          </button>

          {/* Products */}
          <button
            onClick={() => {
              const productsSection = document.getElementById('products');
              if (productsSection) {
                productsSection.scrollIntoView({ behavior: 'smooth', block: 'start' });
                setActiveSection('products');
              }
            }}
            className={`flex flex-col items-center justify-center flex-1 h-full transition-all relative ${activeSection === 'products' ? 'text-orange-600' : 'text-gray-500'
              }`}
          >
            <svg className={`w-6 h-6 mb-1 ${activeSection === 'products' ? 'transform scale-110' : ''} transition-transform`} fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4" />
            </svg>
            <span className="text-xs font-medium bengali-text">পণ্য</span>
            {activeSection === 'products' && (
              <div className="absolute bottom-0 left-1/2 transform -translate-x-1/2 w-12 h-1 bg-orange-600 rounded-t-full"></div>
            )}
          </button>

          {/* Order Track */}
          <button
            onClick={() => setIsTrackingModalOpen(true)}
            className="flex flex-col items-center justify-center flex-1 h-full text-gray-500 transition-all hover:text-orange-600"
          >
            <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z" />
            </svg>
            <span className="text-xs font-medium bengali-text">ট্র্যাক</span>
          </button>

          {/* Cart */}
          <button
            onClick={() => setIsCartOpen(true)}
            className="flex flex-col items-center justify-center flex-1 h-full text-gray-500 transition-all hover:text-orange-600 relative"
          >
            <svg className="w-6 h-6 mb-1" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 3h2l.4 2M7 13h10l3-8H6.4M7 13L5.4 5M7 13l-2 9m12-9l2 9M9 22a1 1 0 100-2 1 1 0 000 2zm8 0a1 1 0 100-2 1 1 0 000 2z" />
            </svg>
            <span className="text-xs font-medium bengali-text">ঝুড়ি</span>
            {cartCount > 0 && (
              <span className="absolute top-1 right-3 bg-orange-600 text-white text-[10px] rounded-full px-1.5 py-0.5">{cartCount}</span>
            )}
          </button>

          {/* WhatsApp */}
          <button
            onClick={handleWhatsAppClick}
            className="flex flex-col items-center justify-center flex-1 h-full text-gray-500 transition-all hover:text-green-600"
          >
            <svg className="w-6 h-6 mb-1" fill="currentColor" viewBox="0 0 24 24">
              <path d="M.057 24l1.687-6.163c-1.041-1.804-1.588-3.849-1.587-5.946.003-6.556 5.338-11.891 11.893-11.891 3.181.001 6.167 1.24 8.413 3.488 2.245 2.248 3.481 5.236 3.48 8.414-.003 6.557-5.338 11.892-11.893 11.892-1.99 0-3.951-.488-5.688-1.44l-6.305 1.654zm6.597-3.807c1.676.995 3.276 1.591 5.392 1.592 5.448 0 9.886-4.434 9.889-9.885.002-2.55-.978-4.95-2.756-6.77-1.778-1.82-4.144-2.82-6.694-2.82-5.448 0-9.886 4.434-9.889 9.885-.001 2.55.978 4.95 2.756 6.77 1.778 1.82 4.144 2.82 6.694 2.82zm11.387-5.464c-.074-.124-.272-.198-.57-.347-.297-.149-1.758-.868-2.031-.967-.272-.099-.47-.149-.669.149-.198.297-.768.967-.941 1.165-.173.198-.347.223-.644.074-.297-.149-1.255-.462-2.39-1.475-.883-.788-1.48-1.761-1.653-2.059-.173-.297-.018-.458.13-.606.134-.133.297-.347.446-.521.151-.172.2-.296.3-.495.099-.198.05-.372-.025-.521-.075-.148-.669-1.611-.916-2.206-.242-.579-.487-.501-.669-.51l-.57-.01c-.198 0-.52.074-.792.372s-1.04 1.016-1.04 2.479 1.065 2.876 1.213 3.074c.149.198 2.095 3.2 5.076 4.487.709.306 1.263.489 1.694.626.712.226 1.36.194 1.872.118.571-.085 1.758-.719 2.006-1.413.248-.695.248-1.29.173-1.414z" />
            </svg>
            <span className="text-xs font-medium bengali-text">যোগাযোগ</span>
          </button>
        </div>
      </nav>

      {/* Order Modal */}
      {isModalOpen && (
        <OrderModal
          product={selectedProduct}
          cartItems={!selectedProduct ? cartItems : []}
          onClose={() => {
            setIsModalOpen(false);
            setSelectedProduct(null);
          }}
        />
      )}

      {/* Product Details Modal */}
      {isDetailsOpen && (
        <ProductDetailsModal
          product={selectedProduct}
          onClose={closeProductModalAndResetUrl}
          onAddToCart={(p) => {
            addToCart(p);
            setIsDetailsOpen(false);
          }}
          onOrderNow={(p) => {
            // switch to order modal
            setIsDetailsOpen(false);
            setIsModalOpen(true);
            setSelectedProduct(p);
          }}
          onNotifyCenter={(message, duration = 5000) => showCenterToast(message, duration)}
        />
      )}

      {/* Cart Modal */}
      {isCartOpen && (
        <CartModal
          items={cartItems}
          onClose={() => setIsCartOpen(false)}
          onUpdateQty={updateCartQty}
          onRemoveItem={removeCartItem}
          onCheckout={() => {
            setIsCartOpen(false);
            // Open Order flow with cart
            setSelectedProduct(null);
            setIsModalOpen(true);
          }}
        />
      )}

      {/* Pass addToCart to products */}
      {/* Order Tracking Modal */}
      <OrderTrackingModal
        isOpen={isTrackingModalOpen}
        onClose={() => setIsTrackingModalOpen(false)}
      />
    </div>
  );
}

export default App;
